<template>
    <nav class="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="flex justify-between h-16">
                <div class="flex items-center">
                    <router-link to="/" class="flex items-center">
                        <h1 class="text-xl font-bold text-primary-600 dark:text-primary-400">
                            TwoFiveNine.Club
                        </h1>
                    </router-link>
                    
                    <div class="hidden md:ml-6 md:flex md:space-x-8">
                        <router-link 
                            to="/" 
                            class="nav-link"
                            :class="{ 'nav-link-active': $route.name === 'Dashboard' }"
                        >
                            Dashboard
                        </router-link>
                        <router-link 
                            to="/games" 
                            class="nav-link"
                            :class="{ 'nav-link-active': $route.name === 'AllGames' }"
                        >
                            Games
                        </router-link>
                    </div>
                </div>

                <div class="flex items-center space-x-4">
                    <!-- Loading indicator -->
                    <div v-if="loadingStore?.isLoading?.()" class="flex items-center">
                        <svg class="animate-spin h-5 w-5 text-primary-600 dark:text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                    
                    <div class="relative" ref="userMenuContainer">
                        <button 
                            @click="showUserMenu = !showUserMenu"
                            class="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                            <div class="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-medium">
                                <img 
                                    v-if="authStore.user?.avatar_url" 
                                    :src="authStore.user.avatar_url" 
                                    :alt="authStore.user.username"
                                    class="w-8 h-8 rounded-full object-cover"
                                />
                                <span v-else>{{ userInitial }}</span>
                            </div>
                            <span class="hidden md:block text-sm font-medium">
                                {{ authStore.user?.username }}
                            </span>
                        </button>

                        <div 
                            v-if="showUserMenu"
                            class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700"
                        >
                            <router-link 
                                to="/settings" 
                                class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                @click="showUserMenu = false"
                            >
                                Settings
                            </router-link>
                            <button 
                                @click="handleLogout"
                                class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </nav>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()
let loadingStore: any = null

// Dynamically import loading store to match api.ts pattern
import('@/stores/loading').then(({ useLoadingStore }) => {
    loadingStore = useLoadingStore()
})
const router = useRouter()
const showUserMenu = ref(false)
const userMenuContainer = ref<HTMLElement | null>(null)

const userInitial = computed(() => {
    return authStore.user?.username?.charAt(0).toUpperCase() || '?'
})

const handleLogout = async () => {
    showUserMenu.value = false
    await authStore.logout()
    router.push('/login')
}

// Close menu when clicking outside
const handleClickOutside = (event: Event) => {
    if (showUserMenu.value && userMenuContainer.value && !userMenuContainer.value.contains(event.target as Node)) {
        showUserMenu.value = false
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.nav-link {
    @apply inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300;
}

.nav-link-active {
    @apply border-primary-500 text-gray-900 dark:text-gray-100;
}
</style>